import React, {useCallback, useEffect, useState} from 'react';
import Header from "./Header";
import parse from "html-react-parser";
import allQuotes from "./quotes.json";
import hallucinatedQuotes from "./hallucinated_quotes.json";
import {useSwipeable} from "react-swipeable";
import PropTypes from "prop-types";
import Splash from "./Splash";
import {useNavigate, useLocation} from "react-router-dom";

const backgroundsCount = 16;
const slideDurationSeconds = 12;
const splashDurationMilliseconds = 4000;

const shuffle = (array) => {
    let currentIndex = array.length,  randomIndex;
    while (currentIndex !== 0) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;
        [array[currentIndex], array[randomIndex]] = [
            array[randomIndex], array[currentIndex]];
    }
    return array;
}

export default function Home(props) {
    const navigate = useNavigate();
    const location = useLocation();

    const [quotes] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const hallucinate = params.get('hallucinate') === 'true';
        const sourceQuotes = (hallucinate ? hallucinatedQuotes : allQuotes).map((text, index) => ({ text, id: index }));
        return shuffle([...sourceQuotes]);
    });

    const [currentIndex, setCurrentIndex] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const quoteId = params.get('quote');
        if (quoteId !== null) {
            const id = parseInt(quoteId, 10);
            const index = quotes.findIndex(q => q.id === id);
            if (index !== -1) {
                return index;
            }
        }
        return 0;
    });
    const [paused, setPaused] = useState(false);
    const [splashFinished, setSplashFinished] = useState(false);
    const [splashTimeLeft, setSplashTimeLeft] = useState(splashDurationMilliseconds);
    const [currentSlideTimeLeft, setCurrentSlideTimeLeft] = useState(slideDurationSeconds);
    const [swipingDir, setSwipingDir] = useState(null);
    const [swipeOffset, setSwipeOffset] = useState(0);
    const [showCopied, setShowCopied] = useState(false);
    const [prevBackgroundIndex, setPrevBackgroundIndex] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const quoteId = params.get('quote');
        if (quoteId !== null) {
            const id = parseInt(quoteId, 10);
            const sourceQuotes = (params.get('hallucinate') === 'true' ? hallucinatedQuotes : allQuotes).map((text, index) => ({ text, id: index }));
            const index = sourceQuotes.findIndex(q => q.id === id);
            if (index !== -1) {
                return index;
            }
        }
        return 0;
    });
    const [isTransitioning, setIsTransitioning] = useState(false);

    const complete = (currentIndex + 1) / quotes.length * 100;

    const nextQuote = useCallback(() => {
        const nextIndex = (currentIndex === quotes.length - 1) ? 0 : currentIndex + 1;
        setPrevBackgroundIndex(currentIndex);
        setIsTransitioning(true);
        setSwipeOffset(0);
        setSwipingDir(null);
        setCurrentSlideTimeLeft(slideDurationSeconds);
        setCurrentIndex(nextIndex);
        setTimeout(() => {
            setIsTransitioning(false);
            setPrevBackgroundIndex(nextIndex);
        }, 800);
    }, [quotes.length, currentIndex]);

    const prevQuote = useCallback(() => {
        const nextIndex = (currentIndex === 0) ? quotes.length - 1 : currentIndex - 1;
        setPrevBackgroundIndex(currentIndex);
        setIsTransitioning(true);
        setSwipeOffset(0);
        setSwipingDir(null);
        setCurrentSlideTimeLeft(slideDurationSeconds);
        setCurrentIndex(nextIndex);
        setTimeout(() => {
            setIsTransitioning(false);
            setPrevBackgroundIndex(nextIndex);
        }, 800);
    }, [quotes.length, currentIndex]);

    const shareQuote = useCallback(() => {
        const quoteText = quotes[currentIndex].text.replace(/<br>/g, '\n').replace(/<\/?[^>]+(>|$)/g, "");
        const shareData = {
            title: 'Wheelie Ben Quote',
            text: `"${quoteText}" - Wheelie Ben`,
            url: window.location.href,
        };

        if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
            navigator.share(shareData).catch(console.error);
        } else {
            // Fallback to clipboard
            navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`).then(() => {
                setShowCopied(true);
                setTimeout(() => setShowCopied(false), 2000);
            }).catch(console.error);
        }
    }, [currentIndex, quotes]);

    useEffect(() => {
        if (!splashFinished) return;
        const params = new URLSearchParams(location.search);
        const currentQuoteId = quotes[currentIndex].id;
        if (params.get('quote') !== currentQuoteId.toString()) {
            params.set('quote', currentQuoteId);
            navigate({ search: params.toString() }, { replace: true });
        }
    }, [currentIndex, quotes, navigate, location.search, splashFinished]);

    const handlers = useSwipeable({
        onSwiping: (eventData) => {
            if (eventData.dir === 'Left' || eventData.dir === 'Right') {
                setSwipingDir(eventData.dir);
                setSwipeOffset(eventData.deltaX);
            }
        },
        onSwiped: (eventData) => {
            if (eventData.velocity > 0.3) {
                if (eventData.dir === 'Left') {
                    nextQuote();
                    return;
                }
                if (eventData.dir === 'Right') {
                    prevQuote();
                    return;
                }
            }
            if (eventData.dir === 'Down' && Math.abs(eventData.deltaY) > 50) {
                setPaused(prev => !prev);
            }
            setSwipeOffset(0);
            setSwipingDir(null);
        },
        onTouchStartOrOnMouseDown: () => {
            setPaused(true);
        },
        onTouchEndOrOnMouseUp: () => {
            // Note: setPaused(false) is handled by the timer logic mostly, 
            // but we want to ensure it resumes if it was just a tap/hold
            setPaused(false);
            setSwipeOffset(0);
            setSwipingDir(null);
        },
        delta: 10,
        preventScrollOnSwipe: true,
        trackMouse: true
    });

    useEffect(() => {
        if (paused){
            return;
        }
        const interval = setInterval(() => {
            setCurrentSlideTimeLeft(prev => {
                if (prev <= 0.050) {
                    nextQuote();
                    return slideDurationSeconds;
                }
                return prev - 0.050;
            });
        }, 50);
        return () => {
            clearInterval(interval);
        }
    }, [paused, nextQuote]);

    useEffect(() => {
        const onKeyPress = (e) => {
            if (e.key === 'ArrowRight'){
                nextQuote();
            }
            if (e.key === 'ArrowLeft'){
                prevQuote();
            }
            if (e.key === ' ') {
                setPaused(prev => !prev);
            }
        }
        document.addEventListener('keydown', onKeyPress);
        return () => {
            document.removeEventListener('keydown', onKeyPress);
        }
    }, [nextQuote, prevQuote]);

    useEffect(() => {
        const interval = setInterval(() => {
            setSplashTimeLeft((prev) => {
                if (prev > 0){
                    return prev - 50;
                } else {
                    setSplashFinished(true);
                    clearInterval(interval);
                    return 0;
                }
            });
        }, 50);

        return () => {
            clearInterval(interval);
        }
    }, []);

    useEffect(() => {
        const prefetchImage = (index) => {
            const img = new Image();
            img.src = `/images/bg-${(index % backgroundsCount) + 1}.jpg`;
        };

        // Prefetch next and previous images
        prefetchImage(currentIndex + 1);
        prefetchImage(currentIndex === 0 ? quotes.length - 1 : currentIndex - 1);
    }, [currentIndex, quotes.length]);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    if (splashFinished === false){
        return <Splash splashTime={splashDurationMilliseconds} splashTimeLeft={splashTimeLeft} />;
    }

    const invert = false;

    const isLongQuote = quotes[currentIndex].text.length > 110;
    const quoteStyle = {
        fontSize: (isLongQuote && isMobile) ? "2em" : "3em",
        transform: swipingDir ? `translateX(${-swipeOffset}px) rotate(${-swipeOffset / 20}deg)` : 'none',
        opacity: swipingDir ? Math.max(0.5, 1 - Math.abs(swipeOffset) / 500) : 1,
        transition: swipingDir ? 'none' : 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 0.3s ease-out, font-size 0.3s ease',
        cursor: swipingDir ? 'grabbing' : 'grab'
    };

    const currentBg = `/images/bg-${(currentIndex % backgroundsCount) + 1}.jpg`;
    const prevBg = `/images/bg-${(prevBackgroundIndex % backgroundsCount) + 1}.jpg`;

    return (
        <div className="wrap">
            {/* Background Layers for Cross-fade */}
            <div className="background-blur">
                <div 
                    className="background-layer active" 
                    style={{ backgroundImage: `url('${prevBg}')`, zIndex: 1 }}
                />
                <div 
                    className={`background-layer ${isTransitioning ? 'active' : ''}`} 
                    style={{ backgroundImage: `url('${currentBg}')`, zIndex: 2 }}
                />
            </div>

            <div className="background-main">
                <div 
                    className="background-layer main active" 
                    style={{ backgroundImage: `url('${prevBg}')`, zIndex: 1 }}
                />
                <div 
                    className={`background-layer main ${isTransitioning ? 'active' : ''}`} 
                    style={{ backgroundImage: `url('${currentBg}')`, zIndex: 2 }}
                />
            </div>

            <Header navOpen={props.navOpen} toggleNav={props.toggleNav} page="Home" />
            <div className={invert ? 'container invert' : 'container'}>
                <div className="row d-flex flex-column justify-content-center align-items-center quote-row" {...handlers}>
                    <div className="Quote col-sm-offset-3 col-sm-6 col-md-offset-2 col-md-8 quote" style={quoteStyle}>
                        {parse(quotes[currentIndex].text)}
                        <div className="share-btn" onClick={(e) => { e.stopPropagation(); shareQuote(); }} title="Share Quote">
                            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="18" cy="5" r="3"></circle>
                                <circle cx="6" cy="12" r="3"></circle>
                                <circle cx="18" cy="19" r="3"></circle>
                                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                            </svg>
                        </div>
                        <div className={`copied-badge ${showCopied ? 'show' : ''}`}>Copied!</div>
                    </div>
                </div>
                <div className="row align-items-center">
                    <div className="col-12 pause-button" style={{display: paused ? 'block' : 'none', textAlign: 'center'}}>
                        <img src="/images/pause-icon.png" alt="Paused" className="pause-icon"/>
                    </div>
                </div>
            </div>

            <div className="navigation-hints">
                <div className="nav-hint left" onClick={prevQuote}>&lt;</div>
                <div className="nav-hint right" onClick={nextQuote}>&gt;</div>
            </div>

            <div className="progress-bar-container page-furniture">
                <div className="progress-bar" style={{ width: `${(currentSlideTimeLeft / slideDurationSeconds) * 100}%` }}></div>
            </div>

            <p className="quote-count page-furniture">
                {`${currentIndex + 1}/${quotes.length}`}
                <br/>
                {`${complete.toFixed(0)}%`}
            </p>
        </div>
    );
}

Home.propTypes = {
    navOpen: PropTypes.bool.isRequired,
    toggleNav: PropTypes.func.isRequired
}